import { first, Observable, of } from 'rxjs';
import { AppError } from '@domain/base/app-error';
import { Cart, CART_ID, LEGOS_ID } from '@domain/models/cart/cart';
import { CartErrors } from '@domain/models/cart/cart-errors';
import { CartRepository } from '@domain/models/cart/gateway/cart-repository';
import { Archetype, Lego, TaskType } from '@domain/models/task/task';

/**
 * Implementation of the CartRepository using local storage.
 */
export class CartRepositoryImpl extends CartRepository {
  get(): Observable<Cart | null> {
    return of(this._getCart()).pipe(first());
  }

  getTasks(): Observable<(Lego | Archetype)[]> {
    const cart = this._getCart();
    if (!cart) {
      return of([]);
    }
    return of(cart.tasks).pipe(first());
  }

  addTask(task: Lego | Archetype): Observable<Cart> {
    const cart = this._getCart();
    if (!cart) {
      throw new AppError(CartErrors.CART_NOT_FOUND);
    }

    const taskExists = cart.tasks.some((l) => l.id === task.id);
    if (taskExists) {
      throw new AppError(CartErrors.TASK_ALREADY_EXISTS);
    }

    cart.tasks.push(task);
    localStorage.setItem(CART_ID, JSON.stringify(cart));

    if ('legos' in task) {
      cart.tasks = cart.tasks.filter(
        (t) =>
          !(t.type === TaskType.Lego && task.legos.some((l) => l.id === t.id))
      );
      localStorage.setItem(CART_ID, JSON.stringify(cart));
      const existingLegos = JSON.parse(localStorage.getItem(LEGOS_ID) || '[]');
      const arquetipoLegoIds = task.legos.map((lego: any) => lego.id);
      const cleanedLegos = existingLegos.filter(
        (lego: any) => !arquetipoLegoIds.includes(lego.id)
      );
      const newLegos = task.legos.map((lego: any) => ({
        id: lego.id,
        name: lego.name,
        archetypeId: task.id
      }));
      const updatedLegos = [...cleanedLegos, ...newLegos];
      localStorage.setItem(LEGOS_ID, JSON.stringify(updatedLegos));
    }
    return of(cart).pipe(first());
  }

  removeTask(taskId: string): Observable<string> {
    const cart = this._getCart();
    if (!cart) {
      throw new AppError(CartErrors.CART_NOT_FOUND);
    }

    const taskToRemove = cart.tasks.find((task) => task.id === taskId);
    cart.tasks = cart.tasks.filter((task) => task.id !== taskId);
    localStorage.setItem(CART_ID, JSON.stringify(cart));
    if (taskToRemove && 'legos' in taskToRemove) {
      const existingLegos = JSON.parse(localStorage.getItem(LEGOS_ID) || '[]');
      const updatedLegos = existingLegos.filter(
        (lego: any) => !taskToRemove.legos.some((l: any) => l.id === lego.id)
      );

      localStorage.setItem(LEGOS_ID, JSON.stringify(updatedLegos));
    }

    return of(taskId).pipe(first());
  }

  clear(): Observable<boolean> {
    localStorage.removeItem(CART_ID);
    localStorage.removeItem(LEGOS_ID);
    return of(true).pipe(first());
  }

  create(): Observable<boolean> {
    localStorage.setItem(CART_ID, JSON.stringify({ tasks: [] }));
    return of(true).pipe(first());
  }

  private _getCart(): Cart | null {
    const storageItem = localStorage.getItem(CART_ID);
    if (!storageItem) {
      return null;
    }

    return JSON.parse(storageItem) as Cart;
  }
}
